import i18next, { type i18n as I18n } from "i18next";
import { initReactI18next } from "react-i18next";
import common from "./resources/common";
import shell from "./resources/shell";
import chat from "./resources/chat";
import inbox from "./resources/inbox";
import cases from "./resources/cases";
import builder from "./resources/builder";
import builderWizard from "./resources/builderWizard";
import governance from "./resources/governance";
import operator from "./resources/operator";
import executive from "./resources/executive";
import models from "./resources/models";
import privacy from "./resources/privacy";
import knowledge from "./resources/knowledge";
import admin from "./resources/admin";
import channels from "./resources/channels";
import clarity from "./resources/clarity";
import glossary from "./resources/glossary";
import home from "./resources/home";
import howItWorks from "./resources/howItWorks";
import help from "./resources/help";
import tour from "./resources/tour";
import palette from "./resources/palette";
import insights from "./resources/insights";
import decisions from "./resources/decisions";
import testing from "./resources/testing";
import triggers from "./resources/triggers";
import tools from "./resources/tools";
import connect from "./resources/connect";
import requests from "./resources/requests";
import spending from "./resources/spending";
import modelsSimple from "./resources/modelsSimple";
import channelsSetup from "./resources/channelsSetup";
import knowledgeSimple from "./resources/knowledgeSimple";
import promptsSimple from "./resources/promptsSimple";
import privacySimple from "./resources/privacySimple";
import pickers from "./resources/pickers";
import calendar from "./resources/calendar";
import caseTypes from "./resources/caseTypes";
import observe from "./resources/observe";
import govplain from "./resources/govplain";
import voice from "./resources/voice";
import graphClear from "./resources/graphClear";
import stepPanel from "./resources/stepPanel";
import addStep from "./resources/addStep";
import agentOverview from "./resources/agentOverview";
import reader from "./resources/reader";
import dataLevels from "./resources/dataLevels";

export const LANGUAGES = ["ar", "en"] as const;
export type Language = (typeof LANGUAGES)[number];
/** RTL-first: Arabic is the default (ADR-0020). */
export const DEFAULT_LANGUAGE: Language = "ar";

import myMemory from "./resources/myMemory";
import builderMemory from "./resources/builderMemory";
import richReply from "./resources/richReply";
import whatHappened from "./resources/whatHappened";
import work from "./resources/work";
import waitStep from "./resources/waitStep";
import agentChannels from "./resources/agentChannels";
import approvalChain from "./resources/approvalChain";
import dataSteps from "./resources/dataSteps";
import dataTables from "./resources/dataTables";
import bulkRuns from "./resources/bulkRuns";
import command from "./resources/command";
import views from "./resources/views";
import officer from "./resources/officer";
const bundles = { common, shell, chat, inbox, cases, builder, governance, operator, executive, models, privacy, knowledge, admin, channels, clarity, glossary, home, howItWorks, help, tour, palette, insights, decisions, builderWizard, testing, triggers, tools, connect, requests, spending, modelsSimple, channelsSetup, knowledgeSimple, promptsSimple, privacySimple, pickers, calendar, caseTypes, observe, govplain, voice, graphClear, stepPanel, addStep, agentOverview, reader, dataLevels, myMemory, builderMemory, richReply, whatHappened, work, waitStep, agentChannels, approvalChain, dataSteps, dataTables, bulkRuns, command, views, officer };
export type Namespace = keyof typeof bundles;
export const NAMESPACES = Object.keys(bundles) as Namespace[];

type Tree = { [k: string]: string | Tree };
/** `{ ar: { ns: {...} }, en: { ns: {...} } }` as i18next expects. */
export const resources: Record<Language, Record<Namespace, Tree>> = {
  ar: Object.fromEntries(NAMESPACES.map((ns) => [ns, bundles[ns].ar])) as unknown as Record<Namespace, Tree>,
  en: Object.fromEntries(NAMESPACES.map((ns) => [ns, bundles[ns].en])) as unknown as Record<Namespace, Tree>,
};

export function isLanguage(v: unknown): v is Language {
  return typeof v === "string" && (LANGUAGES as readonly string[]).includes(v);
}

export function dirOf(lang: Language): "rtl" | "ltr" {
  return lang === "ar" ? "rtl" : "ltr";
}

/**
 * A fresh i18next instance. Every UI string is a key in one of the namespaces; Arabic plurals
 * use the CLDR categories (zero, one, two, few, many, other) via Intl.PluralRules.
 */
export function createI18n(lng: Language = DEFAULT_LANGUAGE): I18n {
  const instance = i18next.createInstance();
  void instance.use(initReactI18next).init({
    lng,
    fallbackLng: "en",
    supportedLngs: [...LANGUAGES],
    ns: NAMESPACES,
    defaultNS: "common",
    resources,
    initAsync: false,
    interpolation: { escapeValue: false }, // React escapes
    returnNull: false,
  });
  return instance;
}
